import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './Tooltip.scss';

const Tooltip = ({ children, content, direction = 'top', className = '', delay = 200, disabled = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef(null);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);

  const showTooltip = () => {
    if (disabled || !content) return;

    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const spacing = 8;

      let newPosition = { top: 0, left: 0 };

      switch (direction) {
        case 'top':
          newPosition = {
            top: triggerRect.top - tooltipRect.height - spacing,
            left: triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
          };
          break;
        case 'bottom':
          newPosition = {
            top: triggerRect.bottom + spacing,
            left: triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
          };
          break;
        case 'left':
          newPosition = {
            top: triggerRect.top + (triggerRect.height - tooltipRect.height) / 2,
            left: triggerRect.left - tooltipRect.width - spacing
          };
          break;
        case 'right':
          newPosition = {
            top: triggerRect.top + (triggerRect.height - tooltipRect.height) / 2,
            left: triggerRect.right + spacing
          };
          break;
        default:
          break;
      }

      // Adjust position to prevent tooltip from going off screen
      const padding = 10;
      newPosition.left = Math.max(padding, Math.min(newPosition.left, window.innerWidth - tooltipRect.width - padding));
      newPosition.top = Math.max(padding, Math.min(newPosition.top, window.innerHeight - tooltipRect.height - padding));

      setPosition(newPosition);
    }
  }, [isVisible, direction]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={`tooltip-wrapper ${className}`}>
      <div
        ref={triggerRef}
        className='tooltip-trigger'
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </div>
      {isVisible && content && (
        <div
          ref={tooltipRef}
          className={`tooltip-content tooltip-${direction}`}
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 9999
          }}
          role='tooltip'
        >
          <div className='tooltip-inner'>{content}</div>
          <div className={`tooltip-arrow tooltip-arrow-${direction}`} />
        </div>
      )}
    </div>
  );
};

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  direction: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  className: PropTypes.string,
  delay: PropTypes.number,
  disabled: PropTypes.bool
};

export default Tooltip;
